import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useModal } from '../../../../components/common';
import AddStoryToTree from './AddStoryModal';
import { AddStoryPayload, Person, UpdateStoryPayload } from '../../../../types';
import {
  useCreateStory,
  useUpdateStory,
  useGetFamilyStories,
  useGetFamilyStoriesById,
  useDeleteFamilyStory,
} from '../../../../hooks/treeHooks';
import StoryDetails from './StoryDetails';
import queryClient from '../../../../core/http/react-query';
import ErrorDisplay from '../../../../components/common/ErrorDisplay';
import { maxFamilyStories } from '../../../../core';
import { useAuth } from '../../../../components';
import useIsAdmin from '../../../../hooks/useIsAdmin';
import useSubscription from '../../../../hooks/useSubscription';
import SubscribeModal from '../../../../components/common/SubscribeModal';
import { truncateText } from '../../../../utils';

type FamilyStoryProps = {
  treeId: string;
  treeMembers?: Person[];
  autoOpenCreate?: boolean;
  isActive?: boolean;
  preselectedStoryId?: string;
};

export function FamilyStory({
  treeId,
  treeMembers = [],
  autoOpenCreate = false,
  isActive = true,
  preselectedStoryId,
}: FamilyStoryProps) {
  const { palette } = useTheme();
  const { openModal, closeModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedStoryId, setSelectedStoryId] = useState<string>('');
  const hasAutoOpenedRef = useRef(false);

  const { currentUser } = useAuth();
  const { isAdmin } = useIsAdmin(currentUser);
  const { isPremium, loading: isSubscriptionLoading } = useSubscription();

  const isPaidUser = isPremium || isAdmin;

  const handleListItemClick = (
    _event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number,
    storyId: string,
  ) => {
    setSelectedIndex(index);
    setSelectedStoryId(storyId);
  };

  const createMutation = useCreateStory();
  const { data, isLoading, isError, error, refetch } = useGetFamilyStories(
    treeId ?? '',
  );
  const {
    data: storyData,
    isLoading: isStoryLoading,
    isError: isStoryError,
    error: storyError,
    refetch: storyReFetch,
  } = useGetFamilyStoriesById(selectedStoryId);
  const deleteStoryMutation = useDeleteFamilyStory(selectedStoryId ?? '');
  const updateStoryMutation = useUpdateStory(selectedStoryId ?? '');

  const familyStories = useMemo(() => {
    if (data) {
      return data as any;
    }
    return null;
  }, [data]);

  const storyDetails = useMemo(() => {
    if (storyData) {
      return storyData as any;
    }
    return null;
  }, [storyData]);

  const isStoryLimitReached = familyStories?.length >= maxFamilyStories;

  useEffect(() => {
    if (!familyStories?.length || selectedStoryId) {
      return;
    }

    setSelectedIndex(0);
    setSelectedStoryId(familyStories[0].id);
  }, [familyStories, selectedStoryId]);

  useEffect(() => {
    if (!preselectedStoryId || !familyStories?.length) {
      return;
    }

    const index = familyStories.findIndex(
      (story: { id: string }) => story.id === preselectedStoryId,
    );
    if (index >= 0) {
      setSelectedIndex(index);
      setSelectedStoryId(preselectedStoryId);
    }
  }, [familyStories, preselectedStoryId]);

  const handleAddStory = useCallback(
    (payload: AddStoryPayload | FormData) => {
      createMutation.mutate(payload, {
        onSuccess: () => {
          enqueueSnackbar('Story added successfully', { variant: 'success' });
          queryClient.refetchQueries({
            queryKey: ['familyStories', treeId],
            exact: true,
          });
          closeModal?.();
        },
        onError: (error: any) => {
          enqueueSnackbar(`Failed to add story: ${error.message}`, {
            variant: 'error',
          });
        },
      });
    },
    [closeModal, createMutation, enqueueSnackbar, treeId],
  );

  function handleUpdateStory(payload: UpdateStoryPayload | FormData): void {
    updateStoryMutation.mutate(payload, {
      onSuccess: () => {
        enqueueSnackbar('Family story updated successfully!', {
          variant: 'success',
        });
        queryClient.refetchQueries({
          queryKey: ['familyStories', selectedStoryId],
          exact: true,
        });
      },
      onError: (error) => {
        enqueueSnackbar('Failed to update family story', {
          variant: 'error',
        });
        console.error('Error updating story:', error);
      },
    });
  }

  function handleDeleteStory() {
    deleteStoryMutation.mutate(undefined, {
      onSuccess: () => {
        enqueueSnackbar('Story deleted successfully!', {
          variant: 'success',
        });
        queryClient.refetchQueries({
          queryKey: ['familyStories', treeId],
          exact: true,
        });
        closeModal?.();
      },
      onError: (error) => {
        enqueueSnackbar('Failed to delete story', {
          variant: 'error',
        });
        console.error('Error deleting story:', error);
      },
    });
  }

  const openCreateStoryModal = useCallback(() => {
    if (isStoryLimitReached && !isPaidUser) {
      return openModal(
        <SubscribeModal
          closeModal={closeModal}
          handleClose={closeModal}
          message={`You can only add up to ${maxFamilyStories} family stories.`}
          title="Add More Family Stories"
        />,
      );
    }

    return openModal(
      <AddStoryToTree
        closeModal={closeModal}
        onAddStory={handleAddStory}
        treeId={treeId}
        treeMembers={treeMembers}
      />,
    );
  }, [
    closeModal,
    handleAddStory,
    isPaidUser,
    isStoryLimitReached,
    openModal,
    treeId,
    treeMembers,
  ]);

  useEffect(() => {
    if (!isActive) {
      closeModal?.();
      hasAutoOpenedRef.current = false;
    }
  }, [isActive, closeModal]);

  useEffect(() => {
    if (
      !isActive ||
      !autoOpenCreate ||
      hasAutoOpenedRef.current ||
      isLoading ||
      isStoryLoading ||
      isSubscriptionLoading
    ) {
      return;
    }

    hasAutoOpenedRef.current = true;
    openCreateStoryModal();
  }, [
    autoOpenCreate,
    isActive,
    isLoading,
    isSubscriptionLoading,
    isStoryLoading,
    isStoryLimitReached,
    isPaidUser,
    openModal,
    closeModal,
    treeId,
    handleAddStory,
    openCreateStoryModal,
  ]);

  if (isLoading || isStoryLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
          mt: '64px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError || isStoryError) {
    return (
      <ErrorDisplay
        error={error || storyError}
        onRetry={refetch || storyReFetch}
      />
    );
  }

  return (
    <Box sx={{ width: '100%', my: 1, pb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          py: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="700">
            Family Stories
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preserve your family voice through meaningful stories.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          startIcon={<AddCircleIcon />}
          sx={{
            alignSelf: { xs: 'stretch', sm: 'flex-start' },
            borderRadius: 2,
            minHeight: 40,
            px: 2,
            boxShadow: 2,
            textTransform: 'none',
            transition:
              'transform 220ms cubic-bezier(0.2, 0, 0, 1), box-shadow 220ms cubic-bezier(0.2, 0, 0, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: 4,
            },
          }}
          onClick={openCreateStoryModal}
          disableElevation
        >
          Add a story
        </Button>
      </Box>
      <Box
        my={3}
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 3,
          lineHeight: '1.8',
          p: 3,
          border: `1px solid ${palette.divider}`,
          boxShadow: '0px 6px 20px rgba(16, 24, 40, 0.06)',
          animation: 'storyIntroFade 220ms cubic-bezier(0.2, 0, 0, 1)',
          '@keyframes storyIntroFade': {
            '0%': { opacity: 0, transform: 'translateY(6px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <Typography fontSize={'1.1rem'} lineHeight={1.6} color="text.primary">
          Your priceless memories, recipes, ceremonies, tradition are worth
          preserving and sharing with loved ones. Your family&apos;s history is
          more than just names and dates; it&apos;s the stories, traditions, and
          experiences that shape who you are. By capturing these moments and
          passing them on, you help ensure that your heritage continues to
          inspire and guide future generations.
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          minHeight: { xs: 'auto', md: '62vh' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: '30%' },
            borderRadius: 3,
            bgcolor: 'secondary.light',
            border: `1px solid ${palette.primary.main}`,
            p: 2,
            boxShadow: '0px 8px 24px rgba(16, 24, 40, 0.08)',
          }}
        >
          <Box mb={2}>
            <Typography variant="h6" fontWeight="600">
              Your stories
            </Typography>
          </Box>
          <Typography variant="body2">
            Select a story from the list to view its details.
          </Typography>
          <List component="nav" aria-label="family stories">
            {!familyStories?.length && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No stories yet. Click Add a story to create the first one.
              </Typography>
            )}
            {familyStories?.map((story: any, index: number) => (
              <ListItemButton
                key={story.id}
                selected={selectedIndex === index}
                onClick={(event) => handleListItemClick(event, index, story.id)}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  border: '1px solid',
                  borderColor:
                    selectedIndex === index ? 'primary.main' : 'transparent',
                  bgcolor:
                    selectedIndex === index
                      ? 'rgba(25, 118, 210, 0.10)'
                      : 'transparent',
                  transition:
                    'transform 220ms cubic-bezier(0.2, 0, 0, 1), box-shadow 220ms cubic-bezier(0.2, 0, 0, 1), border-color 220ms cubic-bezier(0.2, 0, 0, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 1,
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      noWrap
                      fontWeight={selectedIndex === index ? 600 : 500}
                    >
                      {truncateText(story.title, 40)}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
        <Box
          sx={{
            width: { xs: '100%', md: '70%' },
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: `1px solid ${palette.divider}`,
            boxShadow: '0px 10px 26px rgba(16, 24, 40, 0.08)',
            animation: 'storyDetailsFade 220ms cubic-bezier(0.2, 0, 0, 1)',
            '@keyframes storyDetailsFade': {
              '0%': { opacity: 0, transform: 'translateY(6px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <StoryDetails
            story={storyDetails}
            treeMembers={treeMembers}
            onDeleteStory={handleDeleteStory}
            onUpdateStory={handleUpdateStory}
          />
        </Box>
      </Box>
    </Box>
  );
}
